import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../store/index';
import { Layout } from './CoreComponents';
import { getOrderTracking, type OrderTrackingItem } from '../store/slices/erpSlice';
import { AgGridReact } from 'ag-grid-react';
import {
  type ColDef,
  type CellClassParams,
  type ICellRendererParams,
} from 'ag-grid-community';
import BaseGrid from './Shared/BaseGrid';

interface ComparisonRow {
  id: string;
  siparisNo: string;
  cariIsim: string | null;
  stokAdi: string;
  siparisMiktari: string;
  siparisMiktariRaw: number;
  birimFiyat: string;
  birimFiyatRaw: number;
  paraBirim: string;
  teslimTarihi: string | null;
  teslimTarihiRaw: number;
  stharTarih: string | null;
  stharTarihRaw: number;
  faturaNo: string | null;
  enSonKiFaturaTarihi: string | null;
  enSonKiFaturaTarihiRaw: number;
  gelenMiktar: string | null;
  gelenMiktarRaw: number | null;
  gelenBirimFiyat: string | null;
  gelenBirimFiyatRaw: number | null;
  gelenParaBirim: string;
  miktarFarkli: boolean;
  fiyatFarkli: boolean;
}

function formatPrice(value: number, currency: string): string {
  const symbol = currency === 'TL' ? '₺' : currency + ' ';
  return symbol + value.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString('tr-TR');
}

function formatQuantity(value: number | null): string | null {
  if (value == null) return null;
  return value.toLocaleString('tr-TR', { maximumFractionDigits: 2 });
}

function isFiyatFarkli(a: number, b: number | null): boolean {
  if (b == null) return false;
  const tol = 0.01;
  return Math.abs(a - b) > tol;
}

function isMiktarFarkli(siparis: number, gelen: number | null): boolean {
  if (gelen == null) return false;
  return siparis !== gelen;
}

function mapOrderTrackingToRow(item: OrderTrackingItem, index: number): ComparisonRow {
  return {
    id: `${item.siparisNo}-${index}`,
    siparisNo: item.siparisNo,
    cariIsim: item.cariIsim ?? null,
    stokAdi: item.stokAdi,
    siparisMiktari: formatQuantity(item.siparisMiktari) ?? '—',
    siparisMiktariRaw: item.siparisMiktari,
    birimFiyat: formatPrice(item.birimFiyat, item.paraBirim),
    birimFiyatRaw: item.birimFiyat,
    paraBirim: item.paraBirim || '—',
    teslimTarihi: formatDate(item.teslimTarihi),
    teslimTarihiRaw: item.teslimTarihi ? new Date(item.teslimTarihi).getTime() : 0,
    stharTarih: formatDate(item.stharTarih),
    stharTarihRaw: item.stharTarih ? new Date(item.stharTarih).getTime() : 0,
    faturaNo: item.faturaNo ?? null,
    enSonKiFaturaTarihi: formatDate(item.enSonKiFaturaTarihi),
    enSonKiFaturaTarihiRaw: item.enSonKiFaturaTarihi ? new Date(item.enSonKiFaturaTarihi).getTime() : 0,
    gelenMiktar: formatQuantity(item.gelenMiktar),
    gelenMiktarRaw: item.gelenMiktar ?? null,
    gelenBirimFiyat: item.gelenBirimFiyat != null ? formatPrice(item.gelenBirimFiyat, item.gelenParaBirim || item.paraBirim) : null,
    gelenBirimFiyatRaw: item.gelenBirimFiyat ?? null,
    gelenParaBirim: item.gelenParaBirim || '—',
    miktarFarkli: isMiktarFarkli(item.siparisMiktari, item.gelenMiktar),
    fiyatFarkli: isFiyatFarkli(item.birimFiyat, item.gelenBirimFiyat),
  };
}

const miktarCellClass = (params: CellClassParams<ComparisonRow>) =>
  params.data?.miktarFarkli ? 'ag-cell-miktar-fark' : '';

const fiyatCellClass = (params: CellClassParams<ComparisonRow>) =>
  params.data?.fiyatFarkli ? 'ag-cell-fiyat-fark' : '';

const OrderInvoiceComparisonPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const gridRef = useRef<AgGridReact<ComparisonRow>>(null);
  const { data: orderTrackingData, loading: orderTrackingLoading, error: orderTrackingError } = useSelector(
    (s: RootState) => s.erp.orderTracking
  );

  useEffect(() => {
    dispatch(getOrderTracking({}));
  }, [dispatch]);

  const [search, setSearch] = useState('');
  const [diffFilter, setDiffFilter] = useState<'all' | 'miktar' | 'fiyat' | 'any'>('all');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ComparisonRow | null>(null);

  const handleOrderClick = useCallback((row: ComparisonRow) => {
    setSelectedOrder(row);
    setShowInvoiceModal(true);
  }, []);

  const columnDefs = useMemo<ColDef<ComparisonRow>[]>(() => [
    {
      headerName: 'Sipariş No',
      field: 'siparisNo',
      minWidth: 160,
      flex: 1.3,
      cellRenderer: (params: ICellRendererParams<ComparisonRow>) => (
        <span
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer font-medium"
          onClick={() => params.data && handleOrderClick(params.data)}
        >
          {params.value}
        </span>
      ),
    },
    {
      headerName: 'Cari İsim',
      field: 'cariIsim',
      minWidth: 160,
      flex: 1.5,
      valueFormatter: (params) => params.value ?? '—',
      tooltipField: 'cariIsim',
    },
    {
      headerName: 'Stok Adı',
      field: 'stokAdi',
      minWidth: 200,
      flex: 2,
      tooltipField: 'stokAdi',
    },
    {
      headerName: 'Sipariş Miktarı',
      field: 'siparisMiktari',
      minWidth: 120,
      flex: 1,
      cellClass: miktarCellClass,
      comparator: (_a, _b, nodeA, nodeB) =>
        (nodeA.data?.siparisMiktariRaw ?? 0) - (nodeB.data?.siparisMiktariRaw ?? 0),
    },
    {
      headerName: 'Birim Fiyat',
      field: 'birimFiyat',
      minWidth: 130,
      flex: 1,
      cellClass: fiyatCellClass,
      comparator: (_a, _b, nodeA, nodeB) =>
        (nodeA.data?.birimFiyatRaw ?? 0) - (nodeB.data?.birimFiyatRaw ?? 0),
    },
    {
      headerName: 'Teslim Tarihi',
      field: 'teslimTarihi',
      minWidth: 120,
      flex: 1,
      valueFormatter: (params) => params.value ?? '—',
      comparator: (_a, _b, nodeA, nodeB) =>
        (nodeA.data?.teslimTarihiRaw ?? 0) - (nodeB.data?.teslimTarihiRaw ?? 0),
    },
    {
      headerName: 'Sipariş Tarihi',
      field: 'stharTarih',
      minWidth: 120,
      flex: 1,
      valueFormatter: (params) => params.value ?? '—',
      comparator: (_a, _b, nodeA, nodeB) =>
        (nodeA.data?.stharTarihRaw ?? 0) - (nodeB.data?.stharTarihRaw ?? 0),
    },
    {
      headerName: 'Fatura No',
      field: 'faturaNo',
      minWidth: 160,
      flex: 1.3,
      valueFormatter: (params) => params.value ?? '—',
    },
    {
      headerName: 'Son Fatura Tarihi',
      field: 'enSonKiFaturaTarihi',
      minWidth: 140,
      flex: 1,
      valueFormatter: (params) => params.value ?? '—',
      comparator: (_a, _b, nodeA, nodeB) =>
        (nodeA.data?.enSonKiFaturaTarihiRaw ?? 0) - (nodeB.data?.enSonKiFaturaTarihiRaw ?? 0),
    },
    {
      headerName: 'Gelen Miktar',
      field: 'gelenMiktar',
      minWidth: 120,
      flex: 1,
      cellClass: miktarCellClass,
      valueFormatter: (params) => params.value ?? '—',
      comparator: (_a, _b, nodeA, nodeB) =>
        (nodeA.data?.gelenMiktarRaw ?? 0) - (nodeB.data?.gelenMiktarRaw ?? 0),
    },
    {
      headerName: 'Gelen Birim Fiyat',
      field: 'gelenBirimFiyat',
      minWidth: 140,
      flex: 1,
      cellClass: fiyatCellClass,
      valueFormatter: (params) => params.value ?? '—',
      comparator: (_a, _b, nodeA, nodeB) =>
        (nodeA.data?.gelenBirimFiyatRaw ?? 0) - (nodeB.data?.gelenBirimFiyatRaw ?? 0),
    },
  ], [handleOrderClick]);

  const rowData = useMemo(() => {
    const list = orderTrackingData ?? [];
    const q = search.trim().toLowerCase();
    let filtered = list;
    if (q) {
      filtered = filtered.filter(item => {
        const matchSiparis = item.siparisNo.toLowerCase().includes(q);
        const matchFatura = item.faturaNo != null && item.faturaNo.toLowerCase().includes(q);
        const matchStok = item.stokAdi.toLowerCase().includes(q);
        return matchSiparis || matchFatura || matchStok;
      });
    }
    if (diffFilter !== 'all') {
      filtered = filtered.filter(item => {
        const hasMiktar = isMiktarFarkli(item.siparisMiktari, item.gelenMiktar);
        const hasFiyat = isFiyatFarkli(item.birimFiyat, item.gelenBirimFiyat);
        if (diffFilter === 'miktar') return hasMiktar;
        if (diffFilter === 'fiyat') return hasFiyat;
        return hasMiktar || hasFiyat;
      });
    }
    return filtered.map((item, i) => mapOrderTrackingToRow(item, i));
  }, [orderTrackingData, search, diffFilter]);

  const closeInvoiceModal = () => {
    setShowInvoiceModal(false);
    setSelectedOrder(null);
  };

  return (
    <Layout currentScreen="order-invoice-comparison">
      <style>{`
        .ag-cell-miktar-fark {
          background-color: #fef3c7 !important;
          color: #78350f !important;
          font-weight: 500;
        }
        .ag-cell-fiyat-fark {
          background-color: #fce4ec !important;
          color: #881337 !important;
          font-weight: 500;
        }
        .dark .ag-cell-miktar-fark {
          background-color: rgba(217, 119, 6, 0.25) !important;
          color: #fef3c7 !important;
        }
        .dark .ag-cell-fiyat-fark {
          background-color: rgba(225, 29, 72, 0.25) !important;
          color: #fce4ec !important;
        }
      `}</style>
      <div className="max-w-8xl mx-auto h-full flex flex-col min-h-0 pb-4 sm:pb-6">
        {/* Arama */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 mb-2 transition-colors duration-300 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="flex-1 relative">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm" aria-hidden />
              <input
                type="text"
                placeholder="Sipariş no, fatura no veya stok adı ile ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={(e) => e.target.select()}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 text-sm"
              />
            </div>
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 p-1">
              {([
                { key: 'all', label: 'Tümü' },
                { key: 'any', label: 'Farklı Olanlar' },
                { key: 'miktar', label: 'Miktar Farkı' },
                { key: 'fiyat', label: 'Fiyat Farkı' },
              ] as const).map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setDiffFilter(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    diffFilter === opt.key
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => dispatch(getOrderTracking({}))}
              className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex-shrink-0"
            >
              <i className="fas fa-sync-alt mr-2"></i>Yenile
            </button>
          </div>
        </div>

        {/* Loading / Error */}
        {orderTrackingLoading && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center text-gray-500 dark:text-gray-400">
            Veriler yükleniyor...
          </div>
        )}
        {orderTrackingError && !orderTrackingLoading && (
          <div className="bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 p-4 text-red-700 dark:text-red-300">
            {orderTrackingError}
          </div>
        )}

        {/* AG Grid */}
        {!orderTrackingLoading && !orderTrackingError && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 flex flex-col flex-1 min-h-0 max-h-[calc(100vh-180px)] sm:max-h-[calc(100vh-200px)] min-h-[200px]">
          <div className="flex items-center gap-4 px-3 py-2 border-b border-gray-200 dark:border-gray-600 flex-shrink-0 text-xs text-gray-600 dark:text-gray-400">
            <i className="fas fa-info-circle text-gray-400 dark:text-gray-500" aria-hidden />
            <span className="font-medium text-gray-700 dark:text-gray-300">Renk anlamı:</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-amber-200 dark:bg-amber-600/60" aria-hidden />
              <span>Miktar farkı (sipariş ≠ gelen)</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-rose-200 dark:bg-rose-600/60" aria-hidden />
              <span>Fiyat farkı (birim fiyat ≠ gelen)</span>
            </span>
          </div>
          <div className="flex-1 min-h-0">
            <BaseGrid<ComparisonRow>
              ref={gridRef}
              gridStateKey="ag-grid-order-invoice-comparison"
              rowData={rowData}
              columnDefs={columnDefs}
              getRowId={(params) => params.data.id}
              autoSizeStrategy={{ type: 'fitGridWidth' }}
              domLayout="normal"
            />
          </div>
        </div>
        )}
      </div>

      {/* Invoice Modal */}
      {showInvoiceModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-[800px] max-w-[95vw] mx-4 max-h-[90vh] overflow-y-auto transition-colors duration-300">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white transition-colors">
                Sipariş Detayı - {selectedOrder.siparisNo}
              </h3>
              <button 
                onClick={closeInvoiceModal}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg transition-colors duration-300">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3 transition-colors">Sipariş ve Fatura Verileri</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Sipariş No:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.siparisNo}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Cari İsim:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.cariIsim ?? '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Stok Adı:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.stokAdi}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Sipariş Miktarı:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.siparisMiktari}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Birim Fiyat:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.birimFiyat}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Para Birimi:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.paraBirim}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Teslim Tarihi:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.teslimTarihi ?? '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Sipariş Tarihi:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.stharTarih ?? '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Fatura No:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.faturaNo ?? '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">En Son Fatura Tarihi:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.enSonKiFaturaTarihi ?? '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Gelen Miktar:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.gelenMiktar ?? '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Gelen Birim Fiyat:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.gelenBirimFiyat ?? '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Gelen Para Birimi:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedOrder.gelenParaBirim}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default OrderInvoiceComparisonPage;
